/* eslint-enable */
import React, { useEffect, useMemo, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';
import DataTableComponent, { DataTableProps } from './DataTableComponent';
import { OrderDirection } from '../common/enums/order-direction.enum';
import { useErrorToast } from '../hooks/useToast';
import Card from '../layouts/CardLayout';
import LoadingContent from './LoadingContent';
import { SelectItem } from './Select';
import CellLayout from './CellLayout';
import PageHeader from './PageHeader';
import PageLayout from '../layouts/PageLayout';
import Popover from './Popover';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ClientTableBasicProps } from './ClientsTableWithQueryParams';
import DataTableFilters from './DataTableFilters';
import i18n from '../common/config/i18n';
import {
  ClientStatusBackgroundColorMapper,
  ClientStatusMarkerColorMapper,
} from '../common/utils/utils';
import Chip from './Chip';
import StatusWithMarker from './StatusWithMarker';
import Button from './Button';
import { IClient, ClientStatus, IClientDTO } from '../common/interfaces/client.interface';
import {
  useAddClientMutation,
  useUpdateClientMutation,
  useClientsQuery,
} from '../services/clients/client.service';
import ClientsStatusSelect from './ClientStatusSelect';
import ClientModal from './ClientModal';
import { useUserProfile } from '../contexts/user/UserContext';
import { UserRoleEnum } from '../common/interfaces/user-profile.interface';

const ClientTableFilteredHeader: TableColumn<IClient>[] = [
  {
    id: 'id',
    name: 'ID',
    sortable: true,
    grow: 0.15,
    cell: (row: IClient) => (
      <CellLayout>
        <small id={`client_id__${row.id}`} className="cell-layout">
          {row.id}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'name',
    name: 'Name',
    grow: 1,
    sortable: true,
    cell: (row: IClient) => (
      <CellLayout>
        <small id={`client_name__${row.id}`} className="cell-layout">
          {row.name}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'status',
    name: 'Status',
    sortable: true,
    grow: 1,
    cell: (row: IClient) => (
      <CellLayout>
        <Chip bgColor={ClientStatusBackgroundColorMapper[row.status]}>
          <StatusWithMarker
            markerColour={ClientStatusMarkerColorMapper[row.status]}
            textColour={ClientStatusBackgroundColorMapper[row.status]}
            id={`client_status__${row.id}`}
          >
            {i18n.t(`clients:status.${row.status}`)}
          </StatusWithMarker>
        </Chip>
      </CellLayout>
    ),
  },
  {
    id: 'description',
    name: 'Description',
    sortable: true,
    grow: 1,
    cell: (row: IClient) => (
      <CellLayout>
        <small id={`client_description__${row.id}`} className="cell-layout">
          {row.description}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'cui',
    name: 'CUI',
    sortable: true,
    grow: 1,
    cell: (row: IClient) => (
      <CellLayout>
        <small id={`client_cui__${row.id}`} className="cell-layout">
          {row.cui}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'raf_number',
    name: 'RAF Number',
    sortable: true,
    grow: 1,
    cell: (row: IClient) => (
      <CellLayout>
        <small id={`client_raf_number__${row.id}`} className="cell-layout">
          {row.raf_number}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'address',
    name: 'Address',
    sortable: true,
    grow: 1,
    cell: (row: IClient) => (
      <CellLayout>
        <small id={`client_address__${row.id}`} className="cell-layout">
          {row.address}
        </small>
      </CellLayout>
    ),
  },
];

type ClientsTableType<T extends object> = React.FC<ClientTableBasicProps> & {
  DataTableComponent: React.FC<DataTableProps<T>>;
};

const ClientsTable: ClientsTableType<IClient> = ({ query, setQuery }: ClientTableBasicProps) => {
  const { companyId } = useParams();

  const [selectedClient, setSelectedClient] = useState<IClient | null>(null);
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState<boolean>(false);
  const [isEditClientModalOpen, setIsEditClientModalOpen] = useState<boolean>(false);

  const { mutateAsync: addClient } = useAddClientMutation();
  const { mutateAsync: updateClient } = useUpdateClientMutation();

  const [status, setStatus] = useState<SelectItem<string>>();
  const { t } = useTranslation();
  const { user } = useUserProfile();

  const currentCompanyId = useMemo(() => {
    if (user?.role === UserRoleEnum.SUPER_ADMIN && companyId) {
      return companyId;
    } else if (user?.company_id) {
      return user?.company_id;
    } else {
      return '';
    }
  }, [user, companyId]);

  const {
    data: clients,
    isLoading: isClientsLoading,
    error: clientsError,
    refetch,
  } = useClientsQuery(
    +currentCompanyId,
    query?.limit as number,
    query?.page as number,
    query.search,
    query.orderBy,
    query.orderDirection as OrderDirection,
    query.status,
  );

  const navigate = useNavigate();

  const onRowClick = (row: IClient) => {
    navigate(`${row.id}`);
  };

  useEffect(() => {
    if (clientsError) {
      useErrorToast('Eroare la preluarea datelor.');
    }
  }, [clientsError]);

  const buildClientActionColumn = (): TableColumn<IClient> => {
    const openClientMenuItems = [
      {
        label: 'Edit',
        onClick: handleOnEdit,
        itemId: 'request_open__kebab_button',
      },
    ];

    return {
      name: '',
      cell: (row: IClient) => (
        <Popover<IClient>
          row={row}
          items={openClientMenuItems}
          popoverId={`client_${row.id}__kebab_menu`}
          className="right-0"
        />
      ),
      width: '60px',
      // allowOverflow: true,
    };
  };

  const handleOnEdit = (row: IClient) => {
    setSelectedClient(row);
    setIsEditClientModalOpen(true);
  };

  const onChangeRowsPerPage = (limit: number) => {
    setQuery({ limit, page: 1 }, 'replaceIn');
  };

  const onChangePage = (page: number) => {
    setQuery({ page }, 'replaceIn');
  };

  const onSort = (column: TableColumn<IClient>, direction: SortOrder) => {
    setQuery(
      {
        orderBy: column.id as string,
        orderDirection:
          direction.toLocaleUpperCase() === OrderDirection.ASC
            ? OrderDirection.ASC
            : OrderDirection.DESC,
      },
      'replaceIn',
    );
  };

  const onSearch = (search: string) => {
    setQuery({ search }, 'replaceIn');
  };

  const onResetFilters = () => {
    setStatus(undefined);
    setQuery({}, 'push');
  };

  const onStatusChange = (statusItem: SelectItem<string>) => {
    setStatus(statusItem);
    setQuery({ status: statusItem.key as ClientStatus }, 'replaceIn');
  };

  const handleOnAddRequest = async (data: IClientDTO) => {
    if (user && currentCompanyId) {
      addClient(
        { ...data, company_id: `${currentCompanyId}` },
        {
          onSuccess: () => {
            refetch();
            setIsAddClientModalOpen(false);
          },
          onError: () => {
            useErrorToast('Eroare la adăugarea clientului');
          },
        },
      );
    }
  };

  const handleOnEditRequest = async (data: IClientDTO) => {
    updateClient(
      { ...data, status: data.status ? ClientStatus.ACTIVE : ClientStatus.INACTIVE },
      {
        onSuccess: () => {
          refetch();
          setIsEditClientModalOpen(false);
        },
        onError: () => {
          useErrorToast('Eroare la actualizarea clientului');
        },
      },
    );
  };

  return (
    <PageLayout>
      <div className="flex gap-4 justify-between w-full">
        <PageHeader title="Clienti" />
        {user?.role !== UserRoleEnum.EMPLOYEE && (
          <Button
            id="client_add__button"
            className="btn-primary"
            onClick={setIsAddClientModalOpen.bind(null, true)}
            label={'Adauga client'}
          />
        )}
      </div>
      <Card>
        <DataTableFilters
          onSearch={onSearch}
          onResetFilters={onResetFilters}
          searchValue={query.search}
        >
          <ClientsStatusSelect
            id="request_status_filter__select"
            onChange={onStatusChange}
            selected={status}
            className="max-h-52"
          />
        </DataTableFilters>
        <ClientsTable.DataTableComponent
          columns={
            [...ClientTableFilteredHeader, buildClientActionColumn()] as TableColumn<IClient>[]
          }
          pagination
          onSort={onSort}
          onRowClicked={onRowClick}
          data={clients?.items}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          pointerOnHover={true}
          paginationDefaultPage={query.page}
          paginationPerPage={query.limit}
          paginationTotalRows={clients?.meta.totalItems}
          defaultSortAsc={false}
          loading={isClientsLoading}
        />
        {isClientsLoading && <LoadingContent />}
      </Card>
      {isAddClientModalOpen && (
        <ClientModal
          onClose={setIsAddClientModalOpen.bind(null, false)}
          onSubmit={handleOnAddRequest}
          title={t('client_modal:form_name')}
          buttonLabel={t('client_modal:add')}
        />
      )}
      {isEditClientModalOpen && (
        <ClientModal
          onClose={setIsEditClientModalOpen.bind(null, false)}
          onSubmit={handleOnEditRequest}
          title={t('client_modal:form_name')}
          buttonLabel={t('client_modal:edit')}
          client={selectedClient}
        />
      )}
    </PageLayout>
  );
};

ClientsTable.DataTableComponent = DataTableComponent;

export default ClientsTable;
